import React, { FC, useRef, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Video, Participant } from '../../types'
import SpeakerAttributionEditor from './SpeakerAttributionEditor'
import Alert from '@mui/material/Alert'
import SectionWrapper, { SectionStatus } from './LockedSectionWrapper'

interface DiarizationSectionProps {
  video: Video
  participants: Participant[]
  subtitleContentUrl?: string
  subtitleOffset: number
  rotationAdjustment?: number
  speakerLabels: Record<string, string>
  attributions: Record<string, string>
  diarizationProgress: { percent?: number; stage?: string; speed?: string; logs?: string[] } | null
  onStartDiarization: () => void
  isStarting: boolean
  onClearDiarization: () => void
  isClearing: boolean
  onRelabelVoice: (tag: string, participantId: string) => void
  onEditCue: (index: number, text: string, lang: string) => void
  onSplitCue: (index: number, word: number) => void
  onJoinCue: (index: number) => void
  onSetLineSpeaker: (index: number, participantId: string) => void
  isReady: boolean
  // True once this video is sealed (only ever by an explicit seal). The
  // backend refuses StartDiarization, ClearDiarization,
  // UpdateSubtitleCueText and UpdateAttributionEdits once sealed, so every
  // control that would call them freezes here rather than letting an admin
  // type an edit that only fails on save.
  sealed: boolean
}

const DiarizationSection: FC<DiarizationSectionProps> = ({
  video,
  participants,
  subtitleContentUrl,
  subtitleOffset,
  rotationAdjustment,
  speakerLabels,
  attributions,
  diarizationProgress,
  onStartDiarization,
  isStarting,
  onClearDiarization,
  isClearing,
  onRelabelVoice,
  onEditCue,
  onSplitCue,
  onJoinCue,
  onSetLineSpeaker,
  isReady,
  sealed,
}) => {
  const logEndRef = useRef<HTMLDivElement>(null)
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [diarizationProgress?.logs?.length])

  const canDiarize = video.diarization_state !== 'diarizing'

  const hasGroups = (video.voice_groups?.length || 0) > 0

  let status: SectionStatus = 'pending'
  if (video.diarization_state === 'diarizing') status = 'in_progress'
  else if (hasGroups) status = 'complete'

  const diarizeButtonLabel = video.subtitles
    ? 'Auto-attribute speakers'
    : 'Generate subtitles & attribute'

  return (
    <SectionWrapper
      title="Speaker Attribution"
      status={status}
    >
      {/* Sealed notice — the backend refuses a new run, a clear, and every
          cue/label edit below, so say why up front rather than let an admin
          discover it as a 409 after typing. */}
      {sealed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This video is sealed: the manifest attests to the subtitle text and
          speaker attributions exactly as they are below, so neither can ever
          change again. Starting a new run or clearing this one would try to
          overwrite sealed content, so both are frozen — you can still watch,
          browse and jump between every line.
        </Alert>
      )}

      {/* Stream URL required message */}
      {!isReady && canDiarize && !sealed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please paste the 360° stream URL and save before running speaker attribution.
        </Alert>
      )}

      {/* Start / re-run + clear buttons */}
      {isReady && canDiarize && !sealed && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant={hasGroups ? 'outlined' : 'contained'}
            color="secondary"
            onClick={onStartDiarization}
            disabled={isStarting || isClearing}
          >
            {isStarting ? 'Starting...' : hasGroups ? 'Re-run diarization' : diarizeButtonLabel}
          </Button>
          {(hasGroups || video.subtitles) && (
            <Button
              variant="text"
              color="error"
              onClick={() => setConfirmClearOpen(true)}
              disabled={isStarting || isClearing}
            >
              {isClearing ? 'Clearing...' : 'Clear diarization'}
            </Button>
          )}
        </Box>
      )}

      {/* Clear confirmation */}
      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Clear diarization?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the subtitles, voice groups, speaker labels and all
            per-line corrections for this video, returning it to a clean slate.
            A fresh run will regenerate everything from the audio. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmClearOpen(false)
              onClearDiarization()
            }}
          >
            Clear everything
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress */}
      {video.diarization_state === 'diarizing' && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {diarizationProgress?.stage || 'Processing...'}
            </Typography>
          </Box>
          {(diarizationProgress?.percent !== undefined && diarizationProgress.percent > 0) && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress variant="determinate" value={diarizationProgress.percent} />
              <Typography variant="caption" color="text.secondary">
                {diarizationProgress.percent}%
              </Typography>
            </Box>
          )}
          {diarizationProgress?.logs && diarizationProgress.logs.length > 0 && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: '#1a1a1a', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {diarizationProgress.logs.map((line, i) => (
                <div key={i} style={{ color: '#aaa', whiteSpace: 'pre' }}>{line}</div>
              ))}
              <div ref={logEndRef} />
            </Box>
          )}
        </Box>
      )}

      {/* Failed state */}
      {video.diarization_state === 'diarize_failed' && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ErrorOutlineIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error">
              Diarization failed: {video.diarization_error || 'Unknown error'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Editor */}
      {hasGroups && video.diarization_state !== 'diarizing' && (
        <Box>
          <SpeakerAttributionEditor
            video={video}
            subtitleContentUrl={subtitleContentUrl}
            participants={participants}
            voiceGroups={video.voice_groups || []}
            speakerLabels={speakerLabels}
            attributions={attributions}
            subtitleOffset={subtitleOffset}
            rotationAdjustment={rotationAdjustment}
            onRelabelVoice={onRelabelVoice}
            onEditCue={onEditCue}
            onSplitCue={onSplitCue}
            onJoinCue={onJoinCue}
            onSetLineSpeaker={onSetLineSpeaker}
            sealed={sealed}
          />
        </Box>
      )}
    </SectionWrapper>
  )
}

export default DiarizationSection
